import React__default from 'react';
import styled from 'styled-components';
import { useAsyncList } from '@react-stately/data';
import { AkSearchIcon } from '@fuegokit/fuegoicons-react';
import AutocompleteInput from './_AutocompleteInput.js';
import Combobox from '../../components/Combobox/Combobox.js';
import Box from '../../components/Box/Box.js';
import VisuallyHidden from '../../components/VisuallyHidden/VisuallyHidden.js';
import { get } from '../../constants.js';

function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
const Autocomplete = ({
  label,
  children,
  ...props
}) => {
  const list = useAsyncList({
    async load({
      signal,
      filterText
    }) {
      const res = await fetch(`https://swapi.py4e.com/api/people/?search=${filterText}`, {
        signal
      });
      const json = await res.json();
      return {
        items: json.results
      };
    }
  });
  return /*#__PURE__*/React__default.createElement(React__default.Fragment, null, /*#__PURE__*/React__default.createElement(Combobox, {
    label: label,
    items: list.items,
    inputValue: list.filterText,
    onInputChange: list.setFilterText
  }, /*#__PURE__*/React__default.createElement(Combobox.Label, null, label), /*#__PURE__*/React__default.createElement(Box, {
    sx: {
      display: 'flex'
    }
  }, /*#__PURE__*/React__default.createElement(AutocompleteButton, null, /*#__PURE__*/React__default.createElement(VisuallyHidden, null, "Search"), /*#__PURE__*/React__default.createElement(AkSearchIcon, null)), /*#__PURE__*/React__default.createElement(AutocompleteInput, null)), /*#__PURE__*/React__default.createElement(AutocompletePopover, null, /*#__PURE__*/React__default.createElement(AutocompleteListbox, null, item => /*#__PURE__*/React__default.createElement(AutocompleteItem, {
    id: item.name
  }, item.name)))));
};

// Complex Combobox Item

const ComplexAutocompleteItem = ({
  UNSAFE_className,
  leadingVisual: LeadingVisual,
  children,
  label,
  description,
  ...props
}) => {
  return /*#__PURE__*/React__default.createElement(Combobox.Item, _extends({
    className: UNSAFE_className
  }, props), /*#__PURE__*/React__default.createElement(InnerVisualSlot, null, typeof LeadingVisual === 'function' ? /*#__PURE__*/React__default.createElement(LeadingVisual, null) : LeadingVisual), children, label && description ? /*#__PURE__*/React__default.createElement(Box, {
    sx: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between'
    }
  }, /*#__PURE__*/React__default.createElement(AutocompleteText, {
    slot: "label"
  }, label), /*#__PURE__*/React__default.createElement(AutocompleteText, {
    slot: "description"
  }, description)) : null);
};
ComplexAutocompleteItem.displayName = "ComplexAutocompleteItem"; // InnerVisualSlot
/** Inner visual slot to house leading visual, trailing visual, counter, and loading spinners */
const InnerVisualSlot = ({
  children
}) => {
  // just render a visual
  return /*#__PURE__*/React__default.createElement("span", {
    className: "combobox-item-visual"
  }, /*#__PURE__*/React__default.createElement(Box, {
    sx: {
      display: 'flex',
      alignItems: 'center',
      gap: 2
    }
  }, children));
};
InnerVisualSlot.displayName = "InnerVisualSlot";
const AutocompleteItem = styled(ComplexAutocompleteItem).withConfig({
  displayName: "Autocomplete__AutocompleteItem",
  componentId: "sc-105v8jv-0"
})(["padding:", " 0;outline:none;cursor:default;color:", ";font-size:", ";position:relative;width:100%;padding:6px 12px 6px 16px;display:flex;align-items:center;gap:4px;&:focus{background:", ";color:", ";}&:hover{background:", ";color:", ";}&[data-focus-visible]{background:", ";color:", ";}&[aria-selected='true']{background-color:", ";color:", ";font-weight:600;box-shadow:inset 2px 0px 0px ", ";&:hover{background:", ";color:", ";}&::before{alt:' ';position:absolute;top:6px;left:2px;}}&[data-pressed]{background-color:", ";color:", ";}&[aria-disabled]{background-color:", ";color:", ";cursor:not-allowed;}@media (forced-colors:active){&:focus{background:Highlight;color:HighlightText;}}"], get('space.2'), get('colors.text.default'), get('fontSizes.3'), get('colors.background.neutral.subtle.hovered'), get('colors.text.default'), get('colors.background.neutral.subtle.hovered'), get('colors.text.default'), get('colors.background.neutral.subtle.hovered'), get('colors.text.default'), get('colors.background.selected.default.hovered'), get('colors.text.selected'), get('colors.border.selected'), get('colors.background.neutral.subtle.hovered'), get('colors.text.default'), get('colors.background.neutral.subtle.pressed'), get('colors.text.default'), get('colors.elevation.surface.default.[default]'), get('colors.text.disabled'));
const AutocompleteText = styled(Combobox.Text).withConfig({
  displayName: "Autocomplete__AutocompleteText",
  componentId: "sc-105v8jv-1"
})(["&[slot='label']{font-size:", ";font-weight:", ";}&[slot='errorMessage']{font-size:", ";color:var(--ds-text-danger);}&[slot='description']{font-size:", ";}&[slot='description']{&[aria-selected='true']{color:", ";}}"], get('fontSizes.3'), get('fontWeights.semibold'), get('fontSizes.1'), get('fontSizes.1'), get('colors.text.selected'));
const AutocompletePopover = styled(Combobox.Popover).withConfig({
  displayName: "Autocomplete__AutocompletePopover",
  componentId: "sc-105v8jv-2"
})(["--background-color:", ";--border-color:", ";border:1px solid var(--border-color);width:var(--trigger-width);box-sizing:border-box;box-shadow:", ";background:var(--background-color);outline:none;border-radius:", ";top:76px;"], get('colors.elevation.surface.default.[default]'), get('colors.border.default'), get('shadows.elevation.shadow.overlay'), get('radii.1'));
const AutocompleteListbox = styled(Combobox.Listbox).withConfig({
  displayName: "Autocomplete__AutocompleteListbox",
  componentId: "sc-105v8jv-3"
})(["--highlight-background:$themeGet('colors.background.neutral.subtle.hovered');--highlight-foreground:", ";--text-color:", ";--text-color-disabled:", ";max-height:inherit;box-sizing:border-box;overflow:auto;outline:none;padding:", " 0;"], get('colors.text.inverse'), get('colors.text.default'), get('colors.text.disabled'), get('space.2'));
const AutocompleteButton = styled(Combobox.Button).withConfig({
  displayName: "Autocomplete__AutocompleteButton",
  componentId: "sc-105v8jv-4"
})(["appearance:none;margin:0;padding:0;border:none;background:transparent;vertical-align:middle;cursor:pointer;text-align:left;font:inherit;user-select:none;align-items:center;color:inherit;margin-right:-2.48rem;font-weight:normal;font-size:", ";text-decoration:none;opacity:0.85;font-weight:", ";padding:", " 8px;border-radius:", ";color:", ";&:hover,&:focus{opacity:1;text-decoration:none;color:", ";}&:active{opacity:1;text-decoration:none;background-color:", ";color:", ";}&:focus,.focus-visible{outline:none;transition-duration:0s,0.2s;box-shadow:0 0 0 2px ", ";}&:focus:not(:focus-visible):not(.focus-visible){box-shadow:0 0 0 2px ", ";}svg{transition:transform 0.15s ease;}&[aria-expanded='true']{display:none;svg{transform:rotate(var(--icon-rotation));}}"], get('fontSizes.3'), get('fontWeights.medium'), get('space.2'), get('radii.1'), get('colors.icon.subtle'), get('colors.icon.default'), get('colors.background.selected.default.[default]'), get('colors.text.selected'), get('colors.border.focused'), get('colors.border.focused'));
var Autocomplete$1 = Autocomplete;

export { AutocompleteButton, AutocompleteItem, AutocompleteListbox, AutocompletePopover, AutocompleteText, ComplexAutocompleteItem, InnerVisualSlot, Autocomplete$1 as default };
